import CustomLabel from "components/Common/CustomLabel"
import { ErrorMessage, Field, Form, Formik } from "formik"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Button, ModalFooter } from "reactstrap"
import { showAlert } from "store/reducers/alertReducer"
import { updateJobApplicationDetails } from "store/actions/authAction"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import * as Yup from "yup"
import { Checkbox } from "primereact/checkbox"
import { getLocalizedName } from "components/Common/Utils"

const initialValues = {
  cityResidence: "",
  qualification: "",
  specialization: "",
  languages: [],
  otherLanguage: "",
  uniformSize: "",
  healthIssues: "",
  reason: "",
  sacredSites: "",
  sites: [],
  sitesCities: {},
  experienceAttachment: "",
}

const UpdateJobApplication = ({
  userDetails,
  dropdownData,
  toggleModal,
  getDetails,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  const dispatch = useDispatch()
  const [formValues, setFormValues] = useState(initialValues)
  const { isLoading } = useSelector(state => state.Login)

  useEffect(() => {
    if (userDetails) {
      const {
        city_id,
        education_qualification_id,
        specialization_id,
        uniform_size_id,
        has_health_issues,
        health_issues,
        has_crowd_management_exp,
        crowd_management_exp,
        language_ids,
        other_language,
      } = userDetails?.job_application || {}
      setFormValues(prev => ({
        cityResidence: city_id,
        qualification: education_qualification_id,
        specialization: specialization_id,
        languages: language_ids ? language_ids?.map(id => Number(id)) : [], // Convert to numbers
        otherLanguage: other_language,
        uniformSize: uniform_size_id,
        healthIssues: has_health_issues ? "Yes" : "No",
        reason: health_issues,
        sacredSites: has_crowd_management_exp ? "Yes" : "No",
        sites: crowd_management_exp?.map(item => Number(item.station)) || [],
        sitesCities:
          crowd_management_exp?.reduce(
            (acc, item) => ({ ...acc, [item.station]: item.job }),
            {}
          ) || {},
        experienceAttachment: "",
      }))
    }
  }, [userDetails])

  const handleOnSubmit = values => {
    const {
      cityResidence,
      qualification,
      specialization,
      languages,
      otherLanguage,
      uniformSize,
      healthIssues,
      reason,
      sacredSites,
      sites,
      sitesCities,
      experienceAttachment,
    } = values
    const crowd_management_exp =
      sites.map(station => ({
        station: String(station), // Convert to string if needed
        job: Number(sitesCities[station]) || null, // Ensure `job` is a number
      })) || []

    const payload = {
      city_id: cityResidence,
      education_qualification_id: qualification,
      specialization_id: specialization,
      uniform_size_id: uniformSize,
      has_health_issues: healthIssues === "Yes" ? 1 : 0,
      health_issues: reason,
      has_crowd_management_exp: sacredSites === "Yes" ? 1 : 0,
      crowd_managment_exp: crowd_management_exp,
      language_ids: languages || [],
      other_language: otherLanguage,
    }

    const data = {
      uuid: userDetails?.user_information?.uuid,
      payload,
    }
    dispatch(updateJobApplicationDetails(data))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          getDetails()
          toggleModal()
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const validationSchema = Yup.object().shape({
    cityResidence: Yup.string().required(t("Field is required")),
    qualification: Yup.string().required(t("Field is required")),
    specialization: Yup.string().required(t("Field is required")),
    // languages: Yup.array()
    //   .min(1, t("Please select a language to continue."))
    //   .required(t("Languages are required")),
    uniformSize: Yup.string().required(t("Field is required")),
    healthIssues: Yup.string().required(t("Field is required")),
    reason: Yup.string().when("healthIssues", (healthIssues, schema) => {
      return healthIssues[0] === "Yes"
        ? schema.required(t("Field is required"))
        : schema.notRequired()
    }),
    sacredSites: Yup.string().required(t("Field is required")),
    sites: Yup.array().when("sacredSites", (sacredSites, schema) => {
      return sacredSites[0] === "Yes"
        ? schema.min(1, t("Please select at least one option"))
        : schema.notRequired()
    }),
    sitesCities: Yup.object().test(
      "sitesCities-required",
      t("Field is required"),
      function (sitesCities) {
        const { sites } = this.parent // Get sites from form values

        if (!sites || sites.length === 0) return true // No sites selected, no need to validate

        // Check if every selected site has a corresponding city selected
        return sites.every(siteId => sitesCities?.[siteId])
      }
    ),
  })

  return (
    <div>
      <Formik
        initialValues={formValues}
        validationSchema={validationSchema}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, setFieldValue }) => {
          const handleCheckChange = value => {
            const currentSites = values.sites
            const newSites = currentSites.includes(value)
              ? currentSites.filter(site => site !== value)
              : [...currentSites, value]
            setFieldValue("sites", newSites)
          }

          return (
            <Form>
              <div className="row">
                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="cityResidence"
                      label={t("City of Residence")}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="cityResidence"
                        className={`form-control ${
                          errors?.cityResidence && touched?.cityResidence
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option hidden label={t("Select City of Residence")} />
                        {dropdownData?.cities?.map(item => (
                          <option
                            value={item?.id}
                            key={item?.id}
                            label={getLocalizedName(item, language)}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="cityResidence"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="qualification"
                      label={t("Educational Qualification")}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="qualification"
                        className={`form-control ${
                          errors?.qualification && touched?.qualification
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option hidden label={t("Educational Qualification")} />
                        {dropdownData?.education_qualifications?.map(item => (
                          <option
                            value={item?.id}
                            key={item?.id}
                            label={getLocalizedName(item, language)}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="qualification"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="specialization"
                      label={t("Specialization")}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="specialization"
                        className={`form-control ${
                          errors?.specialization && touched?.specialization
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option hidden label={t("Select")} />
                        {dropdownData?.spacialization?.map(item => (
                          <option
                            value={item?.id}
                            key={item?.id}
                            label={getLocalizedName(item, language)}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="specialization"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>

                {/* Language Checkboxes */}
                <div className="col-lg-12">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="languages"
                      label={t("Language")}
                      isRequired={true}
                    />
                    <p className="common_sub_title">{t("Multiple Choice")}</p>
                    <div className="d-flex">
                      <div className="row language_label">
                        {dropdownData?.languages?.map(lang => (
                          <div key={lang.id} className="col-lg-3 col-6">
                            <div className="d-flex align-items-center gap-2 mb-2 mt-2">
                              <input
                                type="checkbox"
                                id={"language_" + lang.id}
                                style={{ height: "unset", width: "unset" }}
                                checked={values.languages?.includes(
                                  Number(lang.id)
                                )} // Convert lang.id to number
                                onClick={() => {
                                  const currentLanguages =
                                    values.languages || []
                                  const updatedLanguages =
                                    currentLanguages.includes(lang.id)
                                      ? currentLanguages.filter(
                                          l => l !== lang.id
                                        ) // Remove if already selected
                                      : [...currentLanguages, lang.id] // Add if not selected
                                  setFieldValue("languages", updatedLanguages) // Update Formik state
                                }}
                              />
                              <label
                                htmlFor={"language_" + lang.id}
                                className="checkbox_label mb-0"
                              >
                                {getLocalizedName(lang, language)}
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    {touched.languages && errors.languages && (
                      <div className="text-danger">{errors.languages}</div>
                    )}
                  </div>
                </div>

                {values?.languages?.includes(8) && (
                  <div className="col-lg-12">
                    <div className="form_field">
                      <CustomLabel
                        htmlFor="otherLanguage"
                        label={t("Please add other languages")}
                      />
                      <Field
                        as="textarea"
                        name="otherLanguage"
                        className="form-control"
                      ></Field>
                      <ErrorMessage
                        name="otherLanguage"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                )}

                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="uniformSize"
                      label={t("Uniform Size")}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="uniformSize"
                        className={`form-control ${
                          errors?.uniformSize && touched?.uniformSize
                            ? "is-invalid"
                            : ""
                        }`}
                      >
                        <option hidden label={t("Select")} />
                        {dropdownData?.uniform_sizes?.map(item => (
                          <option
                            value={item?.id}
                            key={item?.id}
                            label={getLocalizedName(item, language)}
                          />
                        ))}
                      </Field>
                      <ErrorMessage
                        name="uniformSize"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                </div>
                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="healthIssues"
                      label={t(
                        "Do you have chronic illnesses or health issues?"
                      )}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="healthIssues"
                        className="form-control"
                      >
                        <option hidden label={t("Select")} />
                        <option value="Yes" label={t("Yes")} />
                        <option value="No" label={t("No")} />
                      </Field>
                    </div>
                    <ErrorMessage
                      name="healthIssues"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>
                {values.healthIssues === "Yes" ? (
                  <div className="col-lg-6">
                    <div className="form_field">
                      <CustomLabel
                        htmlFor="healthIssues"
                        label={t("Please mention your health issues")}
                        isRequired={true}
                      />
                      <Field
                        as="textarea"
                        name="reason"
                        className="form-control"
                      ></Field>
                      <ErrorMessage
                        name="reason"
                        component="div"
                        className="text-danger"
                      />
                    </div>
                  </div>
                ) : null}

                <div className="col-lg-6">
                  <div className="form_field">
                    <CustomLabel
                      htmlFor="sacredSites"
                      label={t(
                        "Have you previously worked in crowd management for the Mashaer Train?"
                      )}
                      isRequired={true}
                    />
                    <div className="position-relative">
                      <Field
                        as="select"
                        name="sacredSites"
                        className="form-control"
                      >
                        <option hidden label={t("Select")} />
                        <option value="Yes" label={t("Yes")} />
                        <option value="No" label={t("No")} />
                      </Field>
                    </div>
                    <ErrorMessage
                      name="sacredSites"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                </div>

                {values.sacredSites === "Yes" && (
                  <div className="row">
                    {dropdownData?.stations?.map(item => (
                      <div key={item.value} className="col-lg-4 mt-3">
                        <div className="d-flex align-items-center gap-2 mb-2 mt-3">
                          <Checkbox
                            inputId={"station_" + item.id}
                            checked={values.sites.includes(item.id)}
                            onClick={() => handleCheckChange(item.id)}
                          />
                          <label
                            htmlFor={"station_" + item.id}
                            className="checkbox_label mb-0"
                          >
                            {getLocalizedName(item, language)}
                          </label>
                        </div>
                        {values?.sites?.includes(item.id) && (
                          <div>
                            <Field
                              as="select"
                              name={`sitesCities.${item.id}`}
                              className={`form-control ${
                                touched?.sitesCities?.[item.id] &&
                                errors?.sitesCities?.[item.id]
                                  ? "is-invalid"
                                  : ""
                              }`}
                            >
                              <option value="" hidden>
                                {t("Select Job Title")}
                              </option>
                              {/* <option value="" disabled>
                            {t("Select your nationality")}
                          </option> */}
                              {dropdownData?.job_titles?.map(option => (
                                <option key={option.id} value={option.id}>
                                  {getLocalizedName(option, language)}
                                </option>
                              ))}
                            </Field>
                            {/* <ErrorMessage
                          name={`sitesCities.${item.value}`}
                          component="div"
                          className="text-danger"
                        /> */}
                          </div>
                        )}
                      </div>
                    ))}
                    <ErrorMessage
                      name="sites"
                      component="div"
                      className="text-danger"
                    />
                  </div>
                )}

                <div className="col-lg-12">
                  <ModalFooter>
                    <Button
                      color="secondary"
                      type="button"
                      onClick={toggleModal}
                    >
                      {t("Close")}
                    </Button>
                    <Button color="success" type="submit" disabled={isLoading}>
                      {t("Update")}
                    </Button>
                  </ModalFooter>
                </div>
              </div>
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default UpdateJobApplication
